import {
  AllMsaDetailsQuery,
  AllMsaZipDetailsQuery,
  CerberusStatsFields,
  DateStr,
  InputMaybe,
  ListingStatsFragment,
  MsaDetailsQuery,
  MsaHcriCoreFragment,
  MsaHpiCoreFragment,
  MsaHpiTimeSeriesFragment,
  MsaHpiTimeSeriesQuery,
  MsaListingStatsTimeSeriesQuery,
  MsaPreviewFragment,
  MsaRpiTimeSeriesQuery,
  PlaceLookupResult,
  PlaceLookupWithListingStatsQuery,
  PropertyType,
  RpiTimeSeriesFragment,
  ZipCoreFragment,
  ZipDetailsQuery,
  ZipHcriCoreFragment,
  ZipHpiCoreFragment,
  ZipHpiTimeSeriesFragment,
  ZipHpiTimeSeriesQuery,
  ZipListingStatsTimeSeriesQuery,
  ZipRpiTimeSeriesQuery,
} from '@hcs/types';
import { hookEnabledError, roundToDecimalPlace } from '@hcs/utils';

import { CERBERUS_STATS_TYPE } from '../constants';
import {
  CerberusStatsCity,
  CerberusStatsHcriCore,
  CerberusStatsHpiCore,
  CerberusStatsHpiTS,
  CerberusStatsHpiTSRecord,
  CerberusStatsListingStats,
  CerberusStatsMsa,
  CerberusStatsMsaCalculatedFields,
  CerberusStatsRentalListingStats,
  CerberusStatsRpiCore,
  CerberusStatsRpiTSRecord,
  CerberusStatsZip,
  ListingStats,
} from '../types';

// Map of US state full names to abbreviations
const STATE_NAME_TO_ABBR: Record<string, string> = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

/**
 * Converts a full US state name to its two-letter abbreviation
 * @param stateName - Full state name (e.g., "California")
 * @returns Two-letter state code (e.g., "CA") or null if not found
 */
const getStateAbbreviation = (
  stateName: string | null | undefined,
): string | null => {
  if (!stateName) {
    return null;
  }
  return STATE_NAME_TO_ABBR[stateName] || null;
};

const getMsaCalculateFields = (
  msaDetails: NonNullable<MsaDetailsQuery['lookupMsaDetails']>,
): CerberusStatsMsaCalculatedFields => {
  const calculatedFields: CerberusStatsMsaCalculatedFields = {};
  const ratio = msaDetails.populationStats?.population?.population1YearRatio;
  if (ratio != null) {
    calculatedFields.netPopulationGrowth = roundToDecimalPlace(ratio - 1, 4);
  }
  return calculatedFields;
};

const getListingStats = (
  listingStatsFragment: ListingStatsFragment | null | undefined,
): ListingStats | null | undefined => {
  if (listingStatsFragment == null) {
    return listingStatsFragment;
  }
  return {
    ...listingStatsFragment,
    listingsPriceDropPercent:
      listingStatsFragment.listingsPriceDropPercent != null
        ? roundToDecimalPlace(
            listingStatsFragment.listingsPriceDropPercent,
            1,
          ) / 100
        : listingStatsFragment.listingsPriceDropPercent,
    listingsPriceDropPercentMovingAvg:
      listingStatsFragment.listingsPriceDropPercentMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.listingsPriceDropPercentMovingAvg,
            1,
          ) / 100
        : listingStatsFragment.listingsPriceDropPercentMovingAvg,
    listingsUnderContractPercent:
      listingStatsFragment.listingsUnderContractPercent != null
        ? roundToDecimalPlace(
            listingStatsFragment.listingsUnderContractPercent,
            1,
          ) / 100
        : listingStatsFragment.listingsUnderContractPercent,
    listingsUnderContractPercentMovingAvg:
      listingStatsFragment.listingsUnderContractPercentMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.listingsUnderContractPercentMovingAvg,
            1,
          ) / 100
        : listingStatsFragment.listingsUnderContractPercentMovingAvg,
    listingsRemovedPercent:
      listingStatsFragment.listingsRemovedPercent != null
        ? roundToDecimalPlace(listingStatsFragment.listingsRemovedPercent, 1) /
          100
        : listingStatsFragment.listingsRemovedPercent,
    listingsRemovedPercentMovingAvg:
      listingStatsFragment.listingsRemovedPercentMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.listingsRemovedPercentMovingAvg,
            1,
          ) / 100
        : listingStatsFragment.listingsRemovedPercentMovingAvg,
    monthsOfSupplyMedian:
      listingStatsFragment.monthsOfSupplyMedian != null
        ? roundToDecimalPlace(listingStatsFragment.monthsOfSupplyMedian, 1)
        : listingStatsFragment.monthsOfSupplyMedian,
    monthsOfSupplyMedianMovingAvg:
      listingStatsFragment.monthsOfSupplyMedianMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.monthsOfSupplyMedianMovingAvg,
            1,
          )
        : listingStatsFragment.monthsOfSupplyMedianMovingAvg,
    saleToListPriceMedian:
      listingStatsFragment.saleToListPriceMedian != null
        ? roundToDecimalPlace(listingStatsFragment.saleToListPriceMedian, 1) /
          100
        : listingStatsFragment.saleToListPriceMedian,
    saleToListPriceMedianMovingAvg:
      listingStatsFragment.saleToListPriceMedianMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.saleToListPriceMedianMovingAvg,
            1,
          ) / 100
        : listingStatsFragment.saleToListPriceMedianMovingAvg,
    // this value comes through as a percentage out of 100 but it's easier to deal with as a decimal
    saleToListPriceOriginalMedian:
      listingStatsFragment.saleToListPriceOriginalMedian != null
        ? roundToDecimalPlace(
            listingStatsFragment.saleToListPriceOriginalMedian,
            1,
          ) / 100
        : listingStatsFragment.saleToListPriceOriginalMedian,
    saleToListPriceOriginalMedianMovingAvg:
      listingStatsFragment.saleToListPriceOriginalMedianMovingAvg != null
        ? roundToDecimalPlace(
            listingStatsFragment.saleToListPriceOriginalMedianMovingAvg,
            1,
          ) / 100
        : listingStatsFragment.saleToListPriceOriginalMedianMovingAvg,
  };
};

const getHpiCore = (
  hpiFragment: MsaHpiCoreFragment | ZipHpiCoreFragment | null | undefined,
): CerberusStatsHpiCore | null | undefined => {
  if (hpiFragment == null) {
    return hpiFragment;
  }
  const return1YearForecast =
    hpiFragment.forecastCalcs?.return?.return1YearForecast;
  const risk1YearLoss = hpiFragment.forecastCalcs?.risk1YearLoss;
  return {
    latestMonth: hpiFragment.timeSeriesCurrent,
    return1YearForecast:
      return1YearForecast != null
        ? roundToDecimalPlace(return1YearForecast, 3)
        : return1YearForecast,
    risk1YearLoss:
      risk1YearLoss != null
        ? roundToDecimalPlace(risk1YearLoss, 3)
        : risk1YearLoss,
  };
};

type RpiCoreWithForecastCalcs = {
  timeSeriesCurrent?: DateStr | null;
  forecastCalcs?: Array<{
    risk1YearLoss?: number | null;
    return1Year?: number | null;
  } | null> | null;
};

const getRpiCore = (
  rpiFragment: RpiCoreWithForecastCalcs | null | undefined,
): CerberusStatsRpiCore | null | undefined => {
  if (rpiFragment == null) {
    return rpiFragment;
  }
  if (rpiFragment?.forecastCalcs) {
    const return1YearForecast = rpiFragment?.forecastCalcs[0]?.return1Year;
    const risk1YearLoss = rpiFragment.forecastCalcs[0]?.risk1YearLoss;
    return {
      latestMonth: rpiFragment.timeSeriesCurrent,
      return1Year:
        return1YearForecast != null
          ? roundToDecimalPlace(return1YearForecast, 3)
          : return1YearForecast,
      risk1YearLoss:
        risk1YearLoss != null
          ? roundToDecimalPlace(risk1YearLoss, 3)
          : risk1YearLoss,
    };
  } else {
    return null;
  }
};

const getHcriCore = (
  hcriFragment: MsaHcriCoreFragment | ZipHcriCoreFragment | null | undefined,
): CerberusStatsHcriCore | null | undefined => {
  if (hcriFragment == null) {
    return hcriFragment;
  }
  return {
    grossYieldMedian: hcriFragment.grossYieldMedian,
  };
};

const getIdFromMsaDetails = (
  msaDetails: NonNullable<MsaDetailsQuery['lookupMsaDetails']>,
): MsaPreviewFragment['msa'] => {
  return msaDetails.msa;
};

export const msaDetailsToCerberusStatsMsa = (
  msaDetails: NonNullable<MsaDetailsQuery['lookupMsaDetails']>,
  propertyType?: InputMaybe<PropertyType>,
): CerberusStatsMsa => {
  // it's much easier to deal with a id that has to be there
  // instead of making all the code take this scenario that shouldn't happen, just going to throw an error
  const id = getIdFromMsaDetails(msaDetails);
  if (!id) {
    throw hookEnabledError('msaDetailsToCerberusStatsMsa', 'id');
  }

  const latestStats =
    propertyType === PropertyType.Condo
      ? msaDetails.listingStatsCondoTownhouse?.latestStats
      : msaDetails.listingStats?.latestStats;
  const rentalLatestStats =
    propertyType === PropertyType.Condo
      ? msaDetails.rentalListingStatsCondoTownhouse?.latestStats
      : msaDetails.rentalListingStats?.latestStats;

  return {
    type: CERBERUS_STATS_TYPE.Msa,
    id,
    displayName: msaDetails.name,
    ...msaDetails,
    listingStats: getListingStats(latestStats),
    rentalListingStats: getListingStats(rentalLatestStats),
    hpi: getHpiCore(msaDetails.hpi),
    hcri: getHcriCore(msaDetails.hcri),
    rpi: getRpiCore(msaDetails.rpi),
    populationStats: msaDetails.populationStats?.population,
    calculatedFields: getMsaCalculateFields(msaDetails),
  };
};

export const msaDetailsQueryToCerberusStatsMsa = (
  msaDetailsQuery: MsaDetailsQuery | null | undefined,
  propertyType?: InputMaybe<PropertyType>,
): CerberusStatsMsa | null | undefined => {
  const lookupMsaDetails = msaDetailsQuery?.lookupMsaDetails;
  if (!lookupMsaDetails) {
    return lookupMsaDetails;
  }
  return msaDetailsToCerberusStatsMsa(lookupMsaDetails, propertyType);
};

export const allMsaDetailsQueryToCerberusStatsMsa = (
  allMsaDetailsQuery: AllMsaDetailsQuery | null | undefined,
): CerberusStatsMsa[] | null => {
  const allMsaDetails = allMsaDetailsQuery?.allMsaDetails;
  if (!allMsaDetails) {
    return null;
  }
  return allMsaDetails.reduce<CerberusStatsMsa[]>((accum, msaDetails) => {
    if (msaDetails) {
      accum.push(
        msaDetailsToCerberusStatsMsa(
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          msaDetails as NonNullable<MsaDetailsQuery['lookupMsaDetails']>,
        ),
      );
    }
    return accum;
  }, []);
};

export const msaListingStatsTSToCerberusListingStats = (
  msaListingStatsTSQuery: MsaListingStatsTimeSeriesQuery | null | undefined,
  propertyType: 'singleFamily' | 'condo' = 'singleFamily',
): {
  saleListingStats: CerberusStatsListingStats[];
  rentalListingStats: CerberusStatsRentalListingStats[];
} | null => {
  const saleTimeSeries =
    propertyType === 'condo'
      ? msaListingStatsTSQuery?.lookupMsaDetails?.listingStatsCondoTownhouse
          ?.timeSeries
      : msaListingStatsTSQuery?.lookupMsaDetails?.listingStats?.timeSeries;
  const rentalTimeSeries =
    propertyType === 'condo'
      ? msaListingStatsTSQuery?.lookupMsaDetails
          ?.rentalListingStatsCondoTownhouse?.timeSeries
      : msaListingStatsTSQuery?.lookupMsaDetails?.rentalListingStats
          ?.timeSeries;
  if (!saleTimeSeries || !rentalTimeSeries) {
    return null;
  }
  return {
    saleListingStats: saleTimeSeries.reduce<CerberusStatsListingStats[]>(
      (accum, record) => {
        const listingStatsRecord = getListingStats(record);
        if (listingStatsRecord) {
          accum.push({
            type: CERBERUS_STATS_TYPE.ListingStats,
            listingStats: listingStatsRecord,
          });
        }
        return accum;
      },
      [],
    ),
    rentalListingStats: rentalTimeSeries.reduce<
      CerberusStatsRentalListingStats[]
    >((accum, record) => {
      const listingStatsRecord = getListingStats(record);
      if (listingStatsRecord) {
        accum.push({
          type: CERBERUS_STATS_TYPE.RentalListingStats,
          rentalListingStats: listingStatsRecord,
        });
      }
      return accum;
    }, []),
  };
};

export const zipListingStatsTSToCerberusListingStats = (
  zipListingStatsTSQuery: ZipListingStatsTimeSeriesQuery | null | undefined,
  propertyType: 'singleFamily' | 'condo' = 'singleFamily',
): CerberusStatsListingStats[] | null => {
  const timeSeries =
    propertyType === 'condo'
      ? zipListingStatsTSQuery?.lookupZipDetails?.listingStatsCondoTownhouse
          ?.timeSeries
      : zipListingStatsTSQuery?.lookupZipDetails?.listingStats?.timeSeries;
  if (!timeSeries) {
    return null;
  }
  return timeSeries.reduce<CerberusStatsListingStats[]>((accum, record) => {
    const listingStatsRecord = getListingStats(record);
    if (listingStatsRecord) {
      accum.push({
        type: CERBERUS_STATS_TYPE.ListingStats,
        listingStats: listingStatsRecord,
      });
    }
    return accum;
  }, []);
};

export const placeLookupListingStatsTSToCerberusListingStats = (
  placeLookupQuery: PlaceLookupWithListingStatsQuery | null | undefined,
  propertyType: 'singleFamily' | 'condo' = 'singleFamily',
): {
  saleListingStats: CerberusStatsListingStats[];
  rentalListingStats: CerberusStatsRentalListingStats[];
} | null => {
  // Return null only when the query has not loaded yet
  if (!placeLookupQuery) {
    return null;
  }
  const saleTimeSeries =
    propertyType === 'condo'
      ? placeLookupQuery?.lookupPlace?.place?.listingStatsCondoTownhouse
          ?.timeSeries
      : placeLookupQuery?.lookupPlace?.place?.listingStats?.timeSeries;

  const rentalTimeSeries =
    propertyType === 'condo'
      ? placeLookupQuery?.lookupPlace?.place?.rentalListingStatsCondoTownhouse
          ?.timeSeries
      : placeLookupQuery?.lookupPlace?.place?.rentalListingStats?.timeSeries;
  // If time series fields are absent (e.g. no condo data for this city),
  // return empty arrays rather than null so XLS generation is not blocked.
  return {
    saleListingStats: (saleTimeSeries ?? []).reduce<
      CerberusStatsListingStats[]
    >((accum, record) => {
      const listingStatsRecord = getListingStats(record);
      if (listingStatsRecord) {
        accum.push({
          type: CERBERUS_STATS_TYPE.ListingStats,
          listingStats: listingStatsRecord,
        });
      }
      return accum;
    }, []),
    rentalListingStats: (rentalTimeSeries ?? []).reduce<
      CerberusStatsRentalListingStats[]
    >((accum, record) => {
      const listingStatsRecord = getListingStats(record);
      if (listingStatsRecord) {
        accum.push({
          type: CERBERUS_STATS_TYPE.RentalListingStats,
          rentalListingStats: listingStatsRecord,
        });
      }
      return accum;
    }, []),
  };
};

const getIdFromZipDetails = (
  zipDetails: NonNullable<ZipDetailsQuery['lookupZipDetails']>,
): ZipCoreFragment['zipcode'] => {
  return zipDetails.zipcode;
};

const zipDetailsToCerberusStatsZip = (
  zipDetails: NonNullable<ZipDetailsQuery['lookupZipDetails']>,
  propertyType?: InputMaybe<PropertyType>,
): CerberusStatsZip => {
  // it's much easier to deal with a id that has to be there
  // instead of making all the code take this scenario that shouldn't happen, just going to throw an error
  const id = getIdFromZipDetails(zipDetails);
  if (!id) {
    throw hookEnabledError('zipDetailsToCerberusStatsZip', 'id');
  }
  const latestStats =
    propertyType === PropertyType.Condo
      ? zipDetails.listingStatsCondoTownhouse?.latestStats
      : zipDetails.listingStats?.latestStats;
  return {
    type: CERBERUS_STATS_TYPE.Zip,
    id,
    displayName: `${
      zipDetails.preferredName ? `${zipDetails.preferredName}, ` : ''
    }${zipDetails.zipcode}`,
    ...zipDetails,
    listingStats: getListingStats(latestStats),
    hpi: getHpiCore(zipDetails.hpi),
    rpi: getRpiCore(zipDetails.rpi),
    hcri: getHcriCore(zipDetails.hcri),
  };
};

export const zipDetailsQueryToCerberusStatsZip = (
  zipDetailsQuery: ZipDetailsQuery | null | undefined,
  propertyType?: InputMaybe<PropertyType>,
): CerberusStatsZip | null | undefined => {
  const lookupZipDetails = zipDetailsQuery?.lookupZipDetails;
  if (!lookupZipDetails) {
    return lookupZipDetails;
  }
  return zipDetailsToCerberusStatsZip(lookupZipDetails, propertyType);
};

export const allMsaZipDetailsQueryToCerberusStatsZip = (
  allMsaZipDetailsQuery: AllMsaZipDetailsQuery | null | undefined,
): CerberusStatsZip[] | null => {
  const allZipDetails = allMsaZipDetailsQuery?.lookupMsaDetails?.zips;
  if (!allZipDetails) {
    return null;
  }
  return allZipDetails.reduce<CerberusStatsZip[]>((accum, zipDetails) => {
    if (zipDetails) {
      accum.push(zipDetailsToCerberusStatsZip(zipDetails));
    }
    return accum;
  }, []);
};

const getHpiTS = (
  hpiTSRecord: MsaHpiTimeSeriesFragment | ZipHpiTimeSeriesFragment,
): CerberusStatsHpiTSRecord => {
  return {
    month: hpiTSRecord.month,
    valueIndexed: hpiTSRecord.valueIndexed,
  };
};

const getRpiTS = (
  rpiTSRecord: RpiTimeSeriesFragment,
): CerberusStatsRpiTSRecord => {
  return {
    month: rpiTSRecord.month,
    valueIndexed: rpiTSRecord.valueIndexed,
  };
};

export const msaHpiTSQueryToCerberusHpiTS = (
  msaHpiTSQuery: MsaHpiTimeSeriesQuery | null | undefined,
): CerberusStatsHpiTS | null => {
  const hpi = msaHpiTSQuery?.lookupMsaDetails?.hpi;
  if (!hpi) {
    return null;
  }
  return {
    historicalTimeSeries: hpi.historicalTimeSeries?.map(getHpiTS),
    forecastTimeSeries: hpi.forecastTimeSeries?.map(getHpiTS),
  };
};

export const msaRpiTSQueryToCerberusRpiTS = (
  msaRpiTSQuery: MsaRpiTimeSeriesQuery | null | undefined,
): CerberusStatsHpiTS | null => {
  const rpi = msaRpiTSQuery?.lookupMsaDetails?.rpi;
  if (!rpi) {
    return null;
  }
  return {
    historicalTimeSeries: rpi.historicalTimeSeries?.map(getRpiTS),
    forecastTimeSeries: rpi.forecastTimeSeries?.map(getRpiTS),
  };
};

export const zipHpiTSQueryToCerberusHpiTS = (
  zipHpiTSQuery: ZipHpiTimeSeriesQuery | null | undefined,
): CerberusStatsHpiTS | null => {
  const hpi = zipHpiTSQuery?.lookupZipDetails?.hpi;
  if (!hpi) {
    return null;
  }
  return {
    historicalTimeSeries: hpi.historicalTimeSeries?.map(getHpiTS),
    forecastTimeSeries: hpi.forecastTimeSeries?.map(getHpiTS),
  };
};

export const zipRpiTSQueryToCerberusRpiTS = (
  zipRpiTSQuery: ZipRpiTimeSeriesQuery | null | undefined,
): CerberusStatsHpiTS | null => {
  const rpi = zipRpiTSQuery?.lookupZipDetails?.rpi;
  if (!rpi) {
    return null;
  }
  return {
    historicalTimeSeries: rpi.historicalTimeSeries?.map(getRpiTS),
    forecastTimeSeries: rpi.forecastTimeSeries?.map(getRpiTS),
  };
};

// Helper: chunk array into pairs
export const chunkIntoRows = (fields: CerberusStatsFields[], size = 2) => {
  const rows: CerberusStatsFields[][] = [];
  for (let i = 0; i < fields.length; i += size) {
    rows.push(fields.slice(i, i + size));
  }
  return rows;
};

// Helper: chunk rows into pages (3 rows per page)
export const chunkIntoPages = (
  rows: CerberusStatsFields[][],
  rowsPerPage = 3,
) => {
  const pages: CerberusStatsFields[][][] = [];
  for (let i = 0; i < rows.length; i += rowsPerPage) {
    pages.push(rows.slice(i, i + rowsPerPage));
  }
  return pages;
};

export const placeLookupToCerberusStatsCity = (
  placeLookupResult: PlaceLookupResult | null | undefined,
  propertyType?: 'singleFamily' | 'condo',
): CerberusStatsCity | undefined | null => {
  const place = placeLookupResult?.place;
  if (!place) {
    return null;
  }

  const latestStats =
    propertyType === 'condo'
      ? place.listingStatsCondoTownhouse?.latestStats
      : place.listingStats?.latestStats;

  // Convert full state name to abbreviation (e.g., "California" -> "CA")
  const stateAbbr = getStateAbbreviation(place.region);

  // Format name as "CityName, State"
  const formattedName =
    place.name && stateAbbr ? `${place.name}, ${stateAbbr}` : place.name || '';

  return {
    type: CERBERUS_STATS_TYPE.City,
    ...place,
    name: formattedName,
    displayName: formattedName,
    placeId: place.placeId,
    placeType: place.placeType,
    listingStats: latestStats ? getListingStats(latestStats) : null,
  };
};
